import { Formik, Form, FormikHelpers } from 'formik'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Bell, Monitor } from 'lucide-react'
import {
  NotificationModalValues,
  notificationValidationSchema,
} from './validation'
import {
  NotificationDisplayType,
  NotificationVariant,
} from '@/redux/types/notifications'

interface NotificationModalProps {
  isOpen: boolean
  onClose: () => void
  onSend?: (
    message: string,
    displayType: NotificationDisplayType,
    variant: NotificationVariant
  ) => void
}

interface DisplayOption {
  value: NotificationDisplayType
  label: string
  description: string
  icon: React.ReactNode
}

interface VariantOption {
  value: NotificationVariant
  label: string
  icon: React.ReactNode
  colorClass: string
}

const displayOptions: DisplayOption[] = [
  {
    value: NotificationDisplayType.BELL_ICON,
    label: 'Bell Notification',
    description:
      'Appears in the notification bell - students can view when convenient',
    icon: <Bell className='w-4 h-4' />,
  },
  {
    value: NotificationDisplayType.MODAL_CENTER_AUTO_DISMISS,
    label: 'Center Modal',
    description: 'Appears in center of screen - demands immediate attention',
    icon: <Monitor className='w-4 h-4' />,
  },
]

const variantOptions: VariantOption[] = [
  {
    value: 'info',
    label: 'Information',
    icon: <img src='/icons/info-icon.svg' alt='' className='w-4 h-4' />,
    colorClass: 'text-blue-600',
  },
  {
    value: 'success',
    label: 'Success',
    icon: <img src='/icons/success-icon.svg' alt='' className='w-4 h-4' />,
    colorClass: 'text-green-600',
  },
  {
    value: 'warning',
    label: 'Warning',
    icon: <img src='/icons/warning-icon.svg' alt='' className='w-4 h-4' />,
    colorClass: 'text-yellow-600',
  },
  {
    value: 'error',
    label: 'Error',
    icon: <img src='/icons/error-icon.svg' alt='' className='w-4 h-4' />,
    colorClass: 'text-red-600',
  },
]

export function NotificationModal({
  isOpen,
  onClose,
  onSend,
}: NotificationModalProps) {
  const handleSendNotification = async (
    values: NotificationModalValues & { variant: NotificationVariant },
    {
      resetForm,
      setSubmitting,
    }: FormikHelpers<NotificationModalValues & { variant: NotificationVariant }>
  ) => {
    try {
      if (onSend) {
        await onSend(values.message, values.displayType, values.variant)
      }
      resetForm()
      onClose()
    } catch (error) {
      console.error('Failed to send notification:', error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[32rem] pt-12 pb-11 max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='text-base font-bold text-[#0F172A]'>
            Send Notification
          </DialogTitle>
          <DialogDescription className='m-0 text-sm text-[#64748B]'>
            Send a message to all room members
          </DialogDescription>
        </DialogHeader>

        <div className='max-w-full space-y-6 mt-6'>
          <Formik
            initialValues={{
              message: '',
              displayType: NotificationDisplayType.BELL_ICON,
              variant: 'info' as NotificationVariant,
            }}
            validationSchema={notificationValidationSchema}
            onSubmit={handleSendNotification}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
              isSubmitting,
              setFieldError,
            }) => (
              <Form>
                <div className='space-y-6'>
                  {/* Message Input */}
                  <div className='space-y-2'>
                    <Label
                      htmlFor='message'
                      className='text-sm font-medium text-[#0F172A]'
                    >
                      Message
                    </Label>
                    <Textarea
                      id='message'
                      className={`placeholder:text-sm placeholder:text-[#99A2B0] bg-[#FAFCFF] min-h-[120px] resize-none ${
                        touched.message && errors.message
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      placeholder='Type your message here...'
                      name='message'
                      value={values.message}
                      onChange={handleChange}
                      onFocus={() => {
                        if (touched.message) {
                          setFieldError('message', undefined)
                        }
                      }}
                      disabled={isSubmitting}
                      maxLength={500}
                    />

                    <div className='flex justify-between items-center'>
                      <div>
                        {touched.message && errors.message && (
                          <div className='flex gap-2 text-sm text-red-500'>
                            <img src='/icons/alert-circle.svg' alt='Warning' />
                            {errors.message}
                          </div>
                        )}
                      </div>
                      <span className='text-xs text-[#64748B]'>
                        {values.message.length}/500
                      </span>
                    </div>
                  </div>

                  {/* Display Type Selection */}
                  <div className='space-y-3'>
                    <Label className='text-sm font-medium text-[#0F172A]'>
                      How should this appear?
                    </Label>
                    <RadioGroup
                      value={values.displayType.toString()}
                      onValueChange={(value) =>
                        setFieldValue('displayType', parseInt(value, 10))
                      }
                      className='space-y-3'
                    >
                      {displayOptions.map((option) => (
                        <div key={option.value} className='relative'>
                          <div
                            className={`flex items-start space-x-3 p-4 rounded-lg border transition-all ${
                              values.displayType === option.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <RadioGroupItem
                              value={option.value.toString()}
                              id={option.value.toString()}
                              className='mt-0.5'
                            />
                            <div className='flex-1 min-w-0'>
                              <Label
                                htmlFor={option.value.toString()}
                                className='flex items-center gap-2 text-sm font-medium text-[#0F172A] cursor-pointer'
                              >
                                {option.icon}
                                {option.label}
                              </Label>
                              <p className='text-xs text-[#64748B] mt-1'>
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>

                  {/* Notification Type Selection */}
                  {values.displayType ===
                    NotificationDisplayType.MODAL_CENTER_AUTO_DISMISS && (
                    <div className='space-y-3'>
                      <Label className='text-sm font-medium text-[#0F172A]'>
                        Notification Type
                      </Label>
                      <RadioGroup
                        value={values.variant}
                        onValueChange={(value) =>
                          setFieldValue('variant', value as NotificationVariant)
                        }
                        className='flex flex-wrap gap-2'
                      >
                        {variantOptions.map((option) => (
                          <div
                            key={option.value}
                            className='flex items-center space-x-2'
                          >
                            <RadioGroupItem
                              value={option.value}
                              id={`variant-${option.value}`}
                            />
                            <Label
                              htmlFor={`variant-${option.value}`}
                              className={`flex items-center gap-1 text-sm font-medium cursor-pointer ${option.colorClass}`}
                            >
                              {option.icon}
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className='flex gap-2 justify-end pt-2'>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={onClose}
                      disabled={isSubmitting}
                      className='border-gray-300 text-gray-700 hover:bg-gray-50'
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      className='bg-dark-blue hover:bg-dark-blue-hovered'
                      disabled={isSubmitting || !values.message.trim()}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Notification'}
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  )
}
